import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, ArrowRightIcon, InfoIcon } from 'lucide-react';
import { useBusiness } from '../BusinessContext';
import { useSetupProgress } from '../../hooks/useSetupProgress';
import { moduleIntros, HubTarget } from '../../data/hubSetup';
interface ModuleIntroProps {
  /** The tab being shown. */
  tab: string;
  catalogLabel: string;
  onNavigate: (tab: string) => void;
}

/**
 * A one-time, one-line explanation at the top of a module: what this page is
 * and where it fits. While setup is unfinished it also carries the way
 * forward, so a merchant who wandered off the path is never stranded.
 */
export function ModuleIntro({
  tab,
  catalogLabel,
  onNavigate
}: ModuleIntroProps) {
  const {
    introsDismissed,
    dismissIntro
  } = useBusiness();
  const {
    next,
    complete
  } = useSetupProgress();
  const key = tab === catalogLabel ? 'catalogue' : tab;
  const intro = moduleIntros[key];
  if (!intro || introsDismissed.includes(key)) return null;
  const resolve = (t: HubTarget) => t === 'catalogue' ? catalogLabel : t;
  // Offer the next step only when it lives somewhere else.
  const onward = !complete && next && resolve(next.target) !== tab ? next : null;
  return <motion.div initial={{
    opacity: 0,
    y: -6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} role="note" className="mb-6 flex items-start gap-3.5 rounded-2xl border border-primary-indigo/15 bg-primary-indigo/[0.03] px-4 py-3.5" data-id="element-2283" data-mp="222x17ao75942">
      <InfoIcon className="w-4 h-4 text-primary-indigo mt-0.5 flex-shrink-0" data-id="element-2284" data-mp="2kty3a7km5kru" />
      <div className="flex-1 min-w-0" data-id="element-2285" data-mp="2kw0qykyan1te">
        <p className="text-sm font-semibold text-dark-navy" data-id="element-2286" data-mp="2pt0yl0askcmm">{intro.title}</p>
        <p className="text-[13px] text-slate-600 mt-0.5 leading-relaxed max-w-3xl" data-id="element-2287" data-mp="2us42uiiy6etj">
          {intro.body}
        </p>
        {onward && <button onClick={() => onNavigate(resolve(onward.target))} className="inline-flex items-center gap-1.5 mt-2 text-[13px] font-semibold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-2288" data-mp="2zrqckhzkhcxl">
            Next step: {onward.title}
            <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-2289" data-mp="2ju5w1910l339" />
          </button>}
      </div>
      <button onClick={() => dismissIntro(key)} aria-label="Dismiss" className="w-7 h-7 -mr-1 -mt-0.5 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2290" data-mp="2f14e0s5ignfg">
        <XIcon className="w-4 h-4" data-id="element-2291" data-mp="32qfhihe8wvxa" />
      </button>
    </motion.div>;
}